import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Clock, CalendarIcon, Users, Armchair, AlertTriangle,
  User, UserPlus, Loader2, StickyNote, X, Minus, Plus,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Table, TerracePreference } from "@/types/table";
import { CustomerData } from "@/types/client";
import { useCustomerNotes } from "@/hooks/useCustomerNotes";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { useEstablishmentQuestions } from "@/hooks/useEstablishmentQuestions";
import { selectApplicableQuestion, normalizeAnswers } from "@/lib/establishmentQuestionUtils";
import { EstablishmentQuestionField } from "@/components/EstablishmentQuestionField";
import { normalizePhoneForSearch } from "@/lib/clientUtils";
import { CreateClientDialog } from "@/components/CreateClientDialog";
import { ChildrenSelector } from "@/components/ChildrenSelector";
import { useToast } from "@/hooks/use-toast";

function parseFreeName(text: string): { firstName: string; lastName: string } {
  const trimmed = text.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const i = trimmed.indexOf(" ");
  if (i === -1) return { firstName: trimmed, lastName: "" };
  return { firstName: trimmed.slice(0, i), lastName: trimmed.slice(i + 1) };
}

interface TimeSlot { start: string; end: string }

export interface QuickReservationPayload {
  customerFirstName: string;
  customerLastName: string;
  customerEmail?: string;
  customerPhone?: string;
  numberOfPeople: number;
  numberOfChildren?: number;
  hasStroller?: boolean;
  terracePreference?: TerracePreference;
  tableId?: string;
  combinedTableId?: string;
  startTime: string;
  date: Date;
  notes?: string;
  internalNotes?: string;
  durationMinutes?: number;
  source?: string;
  customAnswers?: { question: string; answers: string[] };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected table (the one clicked on the floor plan). */
  preselectedTable: Table;
  /** Pre-selected start time (HH:MM). */
  preselectedTime: string;
  /** Pre-selected date. */
  preselectedDate: Date;
  /** Available time slots for the day (start/end). */
  timeSlots: TimeSlot[];
  existingClients?: CustomerData[];
  onReservationCreate: (r: QuickReservationPayload) => Promise<void> | void;
  reservationSettings?: {
    bookingMode?: "slot" | "time";
    reservationDurationMinutes?: number;
    childrenEnabled?: boolean;
    strollerEnabled?: boolean;
    minPeople?: number;
  };
}

export function QuickReservationDialog({
  open, onOpenChange,
  preselectedTable, preselectedTime, preselectedDate,
  timeSlots, existingClients = [],
  onReservationCreate, reservationSettings,
}: Props) {
  const { toast } = useToast();
  const { getNotesForEmail, customerNotes } = useCustomerNotes();

  const childrenEnabled = reservationSettings?.childrenEnabled ?? false;
  const strollerEnabled = reservationSettings?.strollerEnabled ?? false;
  const isTimeMode = reservationSettings?.bookingMode === "time";
  const defaultDuration = reservationSettings?.reservationDurationMinutes ?? 120;
  const minPeople = reservationSettings?.minPeople ?? 1;

  const tableCapacity = preselectedTable.capacity ?? 2;
  // Contexte équipe : on se base sur la capacité réelle de la table,
  // la limite publique (maxPeople) ne s'applique qu'au formulaire client.
  const tableMaxCap = tableCapacity;

  // === State ===
  const [date] = useState<Date>(preselectedDate);
  const [time, setTime] = useState<string>(preselectedTime);
  const [people, setPeople] = useState<number>(Math.min(tableMaxCap, Math.max(minPeople, 2)));
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [customDuration, setCustomDuration] = useState<number | null>(null);

  const [selectedClient, setSelectedClient] = useState<CustomerData | null>(null);
  const [freeTextName, setFreeTextName] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showCreateClientDialog, setShowCreateClientDialog] = useState(false);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [source, setSource] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Question de l'établissement (facultative côté équipe)
  const { establishmentId } = useEstablishment();
  const { questions: establishmentQuestions } = useEstablishmentQuestions({ establishmentId });
  const applicableQuestion = useMemo(
    () => selectApplicableQuestion(establishmentQuestions, date),
    [establishmentQuestions, date]
  );
  const [customAnswers, setCustomAnswers] = useState<string[]>([]);


  // Reset when dialog opens or preselection changes
  useEffect(() => {
    if (!open) return;
    setTime(preselectedTime);
    setPeople(Math.min(tableMaxCap, Math.max(minPeople, 2)));
    setNumberOfChildren(0);
    setHasStroller(false);
    setCustomDuration(null);
    setSelectedClient(null);
    setFreeTextName("");
    setShowClientDropdown(false);
    setNotes("");
    setInternalNotes("");
    setSource("");
    setCustomAnswers([]);
    setIsSubmitting(false);
  }, [open, preselectedTime, preselectedTable.id]);

  // Clamp children when people changes
  useEffect(() => {
    const maxChildren = Math.max(0, people - 1);
    if (numberOfChildren > maxChildren) setNumberOfChildren(maxChildren);
  }, [people]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        clientInputRef.current && !clientInputRef.current.contains(e.target as Node)
      ) setShowClientDropdown(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Time options from the day's slots
  const timeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const s of timeSlots) set.add(s.start.substring(0, 5));
    if (preselectedTime) set.add(preselectedTime);
    return Array.from(set).sort();
  }, [timeSlots, preselectedTime]);

  // Client autocomplete
  const filteredClients = useMemo(() => {
    if (!freeTextName.trim() || selectedClient) return [];
    const q = freeTextName.toLowerCase();
    const qNorm = normalizePhoneForSearch(q);
    return existingClients.filter((c) => {
      const full = `${c.firstName} ${c.lastName}`.toLowerCase();
      const email = c.email?.toLowerCase() || "";
      const phoneNorm = normalizePhoneForSearch(c.phone || "");
      return full.includes(q) || email.includes(q) || (qNorm.length >= 2 && phoneNorm.includes(qNorm));
    }).slice(0, 6);
  }, [freeTextName, existingClients, selectedClient]);

  const clientNoteRecord = useMemo(() => {
    const email = selectedClient?.email;
    if (!email) return null;
    return customerNotes.get(email.toLowerCase()) ?? null;
  }, [selectedClient, customerNotes]);

  const persistentNotes = useMemo(
    () => (selectedClient?.email ? getNotesForEmail(selectedClient.email) : null),
    [selectedClient, getNotesForEmail],
  );

  const canSubmit = !isSubmitting && (selectedClient || freeTextName.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const customer = selectedClient
      ? {
          customerFirstName: selectedClient.firstName,
          customerLastName: selectedClient.lastName,
          customerEmail: selectedClient.email || undefined,
          customerPhone: selectedClient.phone || undefined,
        }
      : (() => {
          const { firstName, lastName } = parseFreeName(freeTextName);
          return { customerFirstName: firstName, customerLastName: lastName };
        })();

    if (!customer.customerFirstName && !customer.customerLastName) {
      toast({
        title: "Erreur de validation",
        description: "Veuillez saisir au moins un prénom ou un nom",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await onReservationCreate({
        ...customer,
        numberOfPeople: childrenEnabled ? Math.max(1, people - numberOfChildren) : people,
        numberOfChildren: childrenEnabled ? numberOfChildren : undefined,
        hasStroller: strollerEnabled ? hasStroller : undefined,
        tableId: preselectedTable.id,
        startTime: time,
        date,
        notes: notes || undefined,
        internalNotes: internalNotes || undefined,
        durationMinutes: customDuration || undefined,
        source: source || undefined,
        ...(applicableQuestion && normalizeAnswers(applicableQuestion, customAnswers).length > 0
          ? {
              customAnswers: {
                question: applicableQuestion.question,
                answers: normalizeAnswers(applicableQuestion, customAnswers),
              },
            }
          : {}),
      });
      onOpenChange(false);
    } catch {
      /* parent handles toast */
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[92vh] sm:max-h-[85vh] min-h-0 flex flex-col overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-1">
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Nouvelle réservation
          </DialogTitle>
        </DialogHeader>

        {/* Sticky recap */}
        <div className="shrink-0 border-b pb-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="text-xs">
              <CalendarIcon className="w-3 h-3 mr-1" />
              {format(date, "EEE d MMM", { locale: fr })}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              <Clock className="w-3 h-3 mr-1" />
              {time}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              <Users className="w-3 h-3 mr-1" />
              {people} pers.{childrenEnabled && numberOfChildren > 0 ? ` (${numberOfChildren} enf.)` : ""}
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Armchair className="w-3 h-3 mr-1" />
              {preselectedTable.name} ({preselectedTable.capacity}p)
            </Badge>
            {isTimeMode && customDuration && (
              <Badge variant="secondary" className="text-xs">{customDuration} min</Badge>
            )}
            {strollerEnabled && hasStroller && (
              <Badge variant="secondary" className="text-xs">🚼 Poussette</Badge>
            )}
          </div>
        </div>

        {/* Scrollable form */}
        <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
          {/* Client */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Client</Label>
            <div className="relative">
              {selectedClient ? (
                <div className="flex items-center h-10 rounded-md border border-input bg-muted/30 px-3 gap-2">
                  <User className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 text-sm font-medium truncate">
                    {selectedClient.firstName} {selectedClient.lastName}
                  </span>
                  {selectedClient.email && (
                    <span className="text-xs text-muted-foreground truncate">{selectedClient.email}</span>
                  )}
                  {selectedClient.guestStatus === "vip" && (
                    <Badge variant="default" className="text-[10px] px-1 py-0 shrink-0">VIP</Badge>
                  )}
                  {selectedClient.isBlacklisted && (
                    <Badge variant="destructive" className="text-[10px] px-1 py-0 shrink-0">Blacklist</Badge>
                  )}
                  <Button
                    variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                    onClick={() => {
                      setSelectedClient(null);
                      setFreeTextName("");
                      setTimeout(() => clientInputRef.current?.focus(), 50);
                    }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="relative flex gap-2">
                  <div className="relative flex-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      ref={clientInputRef}
                      type="text"
                      value={freeTextName}
                      onChange={(e) => { setFreeTextName(e.target.value); setShowClientDropdown(true); }}
                      onFocus={() => freeTextName.trim() && setShowClientDropdown(true)}
                      placeholder="Nom ou téléphone"
                      className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                  </div>
                  <Button
                    type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0"
                    onClick={() => setShowCreateClientDialog(true)}
                    title="Créer un nouveau client"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {showClientDropdown && !selectedClient && freeTextName.trim() && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-md max-h-48 overflow-y-auto"
                >
                  {filteredClients.map((c) => (
                    <button
                      key={c.id || `${c.firstName}-${c.lastName}`}
                      className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors flex items-center gap-2 text-sm"
                      onClick={() => {
                        setSelectedClient(c);
                        setFreeTextName(`${c.firstName} ${c.lastName}`);
                        setShowClientDropdown(false);
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{c.firstName} {c.lastName}</span>
                        {c.phone && <span className="text-muted-foreground ml-2 text-xs">📞 {c.phone}</span>}
                      </div>
                      {c.guestStatus === "vip" && (
                        <Badge variant="default" className="text-[10px] px-1 py-0 shrink-0">VIP</Badge>
                      )}
                      {c.isBlacklisted && (
                        <Badge variant="destructive" className="text-[10px] px-1 py-0 shrink-0">⚠</Badge>
                      )}
                    </button>
                  ))}
                  <button
                    className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors text-sm border-t border-border text-muted-foreground"
                    onClick={() => setShowClientDropdown(false)}
                  >
                    Utiliser « {freeTextName.trim()} »
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Client alerts */}
          {clientNoteRecord && (
            <>
              {clientNoteRecord.isBlacklisted && (
                <Alert className="border-destructive bg-destructive/10">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <AlertDescription className="text-destructive font-medium text-sm">
                    ⚠️ Ce client est blacklisté
                  </AlertDescription>
                </Alert>
              )}
              {clientNoteRecord.hasNoShow && (
                <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-400 font-medium text-sm">
                    Ce client a un historique de no-show
                  </AlertDescription>
                </Alert>
              )}
              {clientNoteRecord.allergiesTags && clientNoteRecord.allergiesTags.length > 0 && (
                <Alert className="border-orange-400 bg-orange-50 dark:bg-orange-950/20">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-700 dark:text-orange-400 text-sm">
                    <span className="font-medium">Allergies :</span> {clientNoteRecord.allergiesTags.join(", ")}
                    {clientNoteRecord.allergiesNotes && (
                      <p className="text-xs mt-1">{clientNoteRecord.allergiesNotes}</p>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {persistentNotes && (
            <div className="p-3 rounded-lg border-2 border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/20">
              <div className="flex items-center gap-2 mb-1">
                <StickyNote className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                <Label className="text-violet-700 dark:text-violet-300 font-medium text-xs">
                  Notes client
                </Label>
              </div>
              <p className="text-sm text-violet-800 dark:text-violet-200 whitespace-pre-wrap">{persistentNotes}</p>
            </div>
          )}

          {/* Time + People */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Horaire</Label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {timeOptions.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Personnes (max {tableMaxCap})
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button" variant="outline" size="icon" className="h-10 w-10"
                  disabled={people <= minPeople}
                  onClick={() => setPeople((p) => Math.max(minPeople, p - 1))}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-8 text-center font-semibold text-base">{people}</span>
                <Button
                  type="button" variant="outline" size="icon" className="h-10 w-10"
                  disabled={people >= tableMaxCap}
                  onClick={() => setPeople((p) => Math.min(tableMaxCap, p + 1))}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Children */}
          {childrenEnabled && people > 1 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Dont enfants</Label>
              <ChildrenSelector
                value={numberOfChildren}
                onChange={setNumberOfChildren}
                max={Math.max(0, people - 1)}
                compact
              />
            </div>
          )}

          {/* Stroller */}
          {strollerEnabled && (
            <div className="flex items-center justify-between">
              <Label htmlFor="qrd-stroller" className="text-sm cursor-pointer">
                🚼 Poussette
              </Label>
              <input
                id="qrd-stroller"
                type="checkbox"
                checked={hasStroller}
                onChange={(e) => setHasStroller(e.target.checked)}
                className="h-4 w-4"
              />
            </div>
          )}

          {/* Custom duration (TIME mode) */}
          {isTimeMode && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Durée de la réservation</Label>
              <Select
                value={customDuration !== null ? customDuration.toString() : "default"}
                onValueChange={(v) => setCustomDuration(v === "default" ? null : parseInt(v))}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Par défaut ({defaultDuration} min)</SelectItem>
                  <SelectItem value="60">1h</SelectItem>
                  <SelectItem value="90">1h30</SelectItem>
                  <SelectItem value="120">2h</SelectItem>
                  <SelectItem value="150">2h30</SelectItem>
                  <SelectItem value="180">3h</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="qrd-notes" className="text-xs">Note de réservation</Label>
            <Input
              id="qrd-notes" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Demande spéciale..."
              maxLength={500}
            />
          </div>

          {applicableQuestion && (
            <EstablishmentQuestionField
              question={applicableQuestion}
              answers={customAnswers}
              onAnswersChange={setCustomAnswers}
              staffMode
              compact
              idPrefix="qrd-question"
            />
          )}


          <div className="space-y-1.5">
            <Label htmlFor="qrd-internal" className="text-xs">Note interne</Label>
            <Textarea
              id="qrd-internal" value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Notes internes pour l'équipe..."
              maxLength={1000}
              className="min-h-[60px]"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="qrd-source" className="text-xs">Source</Label>
            <Select value={source || "none"} onValueChange={(v) => setSource(v === "none" ? "" : v)}>
              <SelectTrigger id="qrd-source" className="h-9">
                <SelectValue placeholder="Sélectionner une source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non renseignée</SelectItem>
                <SelectItem value="Google">Google</SelectItem>
                <SelectItem value="Site web">Site web</SelectItem>
                <SelectItem value="Instagram">Instagram</SelectItem>
                <SelectItem value="Facebook">Facebook</SelectItem>
                <SelectItem value="TheFork">TheFork</SelectItem>
                <SelectItem value="Téléphone">Téléphone</SelectItem>
                <SelectItem value="Passage">Passage</SelectItem>
                <SelectItem value="Bouche à oreille">Bouche à oreille</SelectItem>
                <SelectItem value="Autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="border-t pt-3 mt-auto shrink-0 flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="shrink-0">
            Annuler
          </Button>
          <Button className="flex-1" disabled={!canSubmit} onClick={handleSubmit}>
            {isSubmitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</>
            ) : (
              "Créer la réservation"
            )}
          </Button>
        </div>
      </DialogContent>

      <CreateClientDialog
        open={showCreateClientDialog}
        onOpenChange={setShowCreateClientDialog}
        initialName={freeTextName}
        onClientCreated={(c) => {
          setSelectedClient(c);
          setFreeTextName(`${c.firstName} ${c.lastName}`.trim());
          setShowClientDropdown(false);
        }}
      />
    </Dialog>
  );
}
